<template>
  <v-row>
    <v-col>
      <v-select
        v-if="props.isTypeSelection"
        v-model="selectTypeHour"
        :items="selectTypeHoursList"
        :label="customLabels.hour"
        bg-color="white"
        color="primary"
        variant="outlined"
        density="compact"
        control-variant="stacked"
        inset
        :disabled="disableControls"
        @update:modelValue="handleTimeChange"
      >
        <template v-slot:prepend v-if="prependIconData.showPrependIcon">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-icon color="primary" v-bind="props">
                {{ prependIconData.prependIcon }}
              </v-icon>
            </template>
            <span>{{ prependIconData.prependIconText }}</span>
          </v-tooltip>
        </template>
      </v-select>
      <v-number-input
        v-else
        v-model="hour"
        :label="customLabels.hour"
        bg-color="white"
        color="primary"
        variant="outlined"
        density="compact"
        control-variant="stacked"
        inset
        :step="1"
        :max="format === '12' ? 12 : 23"
        :min="0"
        :disabled="disableControls"
        @update:modelValue="handleTimeChange"
        :hint="format === '12' ? '12 hour format' : '24 hour format'"
        :persistent-hint="showPersistentHint"
        :rules="[
          (v: number) => !!v || 'Hour is required',
          (v: number) =>
            (v >= 0 && v <= (format === '12' ? 12 : 23)) ||
            (format === '12'
              ? 'Hour must be between 0 and 12'
              : 'Hour must be between 0 and 23'),
        ]"
      >
        <template v-slot:prepend v-if="prependIconData.showPrependIcon">
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-icon color="primary" v-bind="props">
                {{ prependIconData.prependIcon }}
              </v-icon>
            </template>
            <span>{{ prependIconData.prependIconText }}</span>
          </v-tooltip>
        </template>
      </v-number-input>
    </v-col>
    <v-col>
      <v-select
        v-if="props.isTypeSelection"
        v-model="selectTypeMinutes"
        :items="selectTypeMinutesList"
        :label="customLabels.minutes"
        bg-color="white"
        color="primary"
        variant="outlined"
        density="compact"
        control-variant="stacked"
        inset
        :disabled="disableControls"
        @update:modelValue="handleTimeChange"
      />
      <v-number-input
        v-else
        v-model="minutes"
        :label="customLabels.minutes"
        bg-color="white"
        color="primary"
        variant="outlined"
        density="compact"
        control-variant="stacked"
        inset
        :step="1"
        :max="59"
        :min="0"
        :disabled="disableControls"
        @update:modelValue="handleTimeChange"
        hint="Minutes"
        :persistent-hint="showPersistentHint"
        :rules="[
          (v: number) => !!v || 'Minutes are required',
          (v: number) => (v >= 0 && v <= 59) || 'Minutes must be between 0 and 59',
        ]"
      >
      </v-number-input>
    </v-col>
    <v-col v-if="showPeriodSelector" cols="2" class="mr-n5">
      <v-select
        v-model="internalFormat"
        :items="items"
        label="Hour Format"
        variant="outlined"
        @update:modelValue="handleTimeChange"
      />
    </v-col>
    <v-col cols="2" v-if="showPeriodSelector && format === '12'">
      <v-select
        v-model="period"
        :items="periods"
        variant="outlined"
        @update:modelValue="handleTimeChange"
      />
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';

const hour = ref<number | null>(null);
const minutes = ref<number | null>(null);
const period = ref('AM');
const items = ref(['24', '12']);
const periods = ref(['AM', 'PM']);
const internalFormat = ref('24');

const selectTypeHour = ref('00');
const selectTypeMinutes = ref('00');
const selectTypeHoursList = ref(
  Array.from({ length: 24 }, (_, i) => {
    return i < 10 ? `0${i}` : `${i}`;
  })
);
const selectTypeMinutesList = ref(
  Array.from({ length: 12 }, (_, i) => {
    const value = i * 5;
    return value < 10 ? `0${value}` : `${value}`;
  })
);

const props = defineProps({
  showPeriodSelector: {
    type: Boolean,
    default: false,
  },
  prependIconData: {
    type: Object,
    default: () => ({
      prependIcon: 'mdi-clock-start',
      prependIconText: 'Start time',
      showPrependIcon: false,
    }),
  },
  format: {
    type: String,
    default: '24',
  },
  presetHour: {
    type: String,
    default: null,
  },
  presetMinutes: {
    type: String,
    default: null,
  },
  showPersistentHint: {
    type: Boolean,
    default: false,
  },
  customLabels: {
    type: Object,
    default: () => ({
      hour: 'Hour',
      minutes: 'Minutes',
      period: 'Period',
    }),
  },
  disableControls: {
    type: Boolean,
    default: false,
  },
  isTypeSelection: {
    type: Boolean,
    default: false,
  },
});

watch(
  () => props.format,
  () => {
    internalFormat.value = props.format;
  }
);

watch(
  () => props.presetHour,
  () => {
    hour.value = Number(props.presetHour);
    selectTypeHour.value = props.isTypeSelection
      ? String(hour.value).padStart(2, '0')
      : '00';
  },
  { immediate: true }
);

watch(
  () => props.presetMinutes,
  () => {
    minutes.value = Number(props.presetMinutes);
    selectTypeMinutes.value = props.isTypeSelection
      ? String(minutes.value).padStart(2, '0')
      : '00';
  },
  { immediate: true }
);

const emits = defineEmits(['time-change']);

const handleTimeChange = () => {
  emits('time-change', {
    hour: !props.isTypeSelection ? hour.value : selectTypeHour.value,
    minutes: !props.isTypeSelection ? minutes.value : selectTypeMinutes.value,
    format: props.showPeriodSelector ? props.format : '',
    period: props.format === '12' ? period.value : '',
  });
};

onMounted(() => {
  handleTimeChange();
});
</script>
